import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import type { AppDispatch } from "@/app/store";
import { updateStepUpState } from "@/modules/auth/store/authSlice";
import useTranslations from "@/shared/lang/useTranslations";
import { StepUpEmailBackupService } from "@/shared/services/stepUpEmailBackup.service";
import { readApiCode, readHumanErrorMessage } from "@/shared/utils/apiError";

/** Where a refusal belongs on screen: under the password, or above the whole form. */
export interface EmailBackupFailure {
  field: "password" | null;
  message: string;
}

export interface UseStepUpEmailBackupResult {
  /** Turns the backup on or off. Resolves with the refusal to show, or null once it is saved. */
  setEnabled: (enabled: boolean, password: string) => Promise<EmailBackupFailure | null>;
  saving: boolean;
}

/**
 * The email backup of the caller's step-up method: turning it on or off with the account password. A success
 * merges the server's step-up state into the signed-in user, so every challenge offers (or stops offering)
 * "send me a code by email" at once.
 */
export function useStepUpEmailBackup(): UseStepUpEmailBackupResult {
  const { t } = useTranslations("global");
  const dispatch = useDispatch<AppDispatch>();
  const [saving, setSaving] = useState(false);

  const failureOf = useCallback(
    (error: unknown): EmailBackupFailure => {
      const code = readApiCode(error);
      if (code === "PASSWORD_INCORRECT") {
        // Wrong current passwords count toward the login lock, so the person is told before the fifth one.
        return { field: "password", message: t("reauth_password_incorrect_hint") };
      }
      if (code === "STEP_UP_EMAIL_REQUIRES_METHOD") {
        return { field: null, message: t("step_up_email_backup_error_requires_method") };
      }
      if (code === "STEP_UP_EMAIL_NOT_AVAILABLE") {
        return { field: null, message: t("step_up_email_backup_error_not_available") };
      }
      // ACCOUNT_LOCKED and anything else: the server's sentence already says what happened and for how long.
      return { field: null, message: readHumanErrorMessage(error, t("step_up_email_backup_error_generic")) };
    },
    [t],
  );

  const setEnabled = useCallback(
    async (enabled: boolean, password: string) => {
      setSaving(true);
      try {
        const state = await StepUpEmailBackupService.set({ enabled, current_password: password });
        dispatch(updateStepUpState(state));
        return null;
      } catch (error: unknown) {
        return failureOf(error);
      } finally {
        setSaving(false);
      }
    },
    [dispatch, failureOf],
  );

  return { setEnabled, saving };
}
